import { toBool, toFiniteCount, toFiniteNumber } from '../formatters';

export const getResultTokenCounts = (result = {}) => {
  const usage = result && typeof result.usage === 'object' ? result.usage : {};
  const input = toFiniteCount(
    result?.input_tokens_consumed ??
      result?.prompt_tokens ??
      usage?.prompt_tokens ??
      usage?.input_tokens,
  );
  const output = toFiniteCount(
    result?.output_tokens_consumed ??
      result?.completion_tokens ??
      usage?.completion_tokens ??
      usage?.output_tokens,
  );
  const total = toFiniteCount(
    result?.total_tokens_consumed ?? result?.total_tokens ?? usage?.total_tokens,
  );
  const hasAnyToken = input !== null || output !== null || total !== null;
  if (!hasAnyToken) {
    return { input: null, output: null, total: null };
  }
  if ((input ?? 0) === 0 && (output ?? 0) === 0 && (total ?? 0) === 0) {
    return { input: null, output: null, total: null };
  }
  const resolvedTotal =
    total !== null ? total : input !== null && output !== null ? input + output : null;
  return { input, output, total: resolvedTotal };
};

const normalizeResultSource = (sourceValue, fallbackCacheHit = false, options = {}) => {
  const allowUnknown = options?.allowUnknown === true;
  const rawSource = String(sourceValue || '').trim().toLowerCase();
  if (rawSource === 'cache' || rawSource === 'provider') {
    return rawSource;
  }
  if (allowUnknown && rawSource === 'unknown') {
    return 'unknown';
  }
  return fallbackCacheHit ? 'cache' : 'provider';
};

export const getSourceDisplayLabel = (sourceValue) => {
  if (sourceValue === 'cache') return 'CACHE';
  if (sourceValue === 'provider') return 'LIVE';
  return 'UNKNOWN';
};

const parseOptionalBoolFlag = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  return toBool(value, false);
};

const REVIEWER_NOTE_SOURCES = new Set(['reviewer', 'human', 'manual', 'user']);
const normalizeNoteCompareText = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export const getTrustedReviewNote = (item = {}) => {
  if (!item || typeof item !== 'object') return '';
  const reviewNote = typeof item.review_note === 'string' ? item.review_note : '';
  const normalizedNote = normalizeNoteCompareText(reviewNote);
  if (!normalizedNote) return '';

  const noteSource = normalizeNoteCompareText(item.review_note_source);
  return REVIEWER_NOTE_SOURCES.has(noteSource) ? reviewNote : '';
};

export const getResultCacheInfo = (result = {}) => {
  const cacheHit = toBool(result?.cache_hit, false);
  const normalizedSource = normalizeResultSource(result?.analysis_source, cacheHit);
  const cacheKey = typeof result?.cache_key === 'string' ? result.cache_key.trim() : '';
  return {
    cacheHit: normalizedSource === 'cache' || cacheHit,
    source: normalizedSource,
    cacheKey,
  };
};

export const getResultAttemptSourceInfo = (result = {}) => {
  const cacheInfo = getResultCacheInfo(result);
  const explicitFinalCacheHit = parseOptionalBoolFlag(result?.final_attempt_cache_hit);
  const explicitFirstCacheHit = parseOptionalBoolFlag(result?.first_attempt_cache_hit);
  const finalCacheHit = explicitFinalCacheHit !== null ? explicitFinalCacheHit : cacheInfo.cacheHit;
  const firstCacheHit = explicitFirstCacheHit !== null ? explicitFirstCacheHit : finalCacheHit;
  const explicitFinalSource =
    typeof result?.final_attempt_source === 'string' ? result.final_attempt_source.trim() : '';
  const explicitFirstSource =
    typeof result?.first_attempt_source === 'string' ? result.first_attempt_source.trim() : '';
  const finalSource = explicitFinalSource
    ? normalizeResultSource(explicitFinalSource, finalCacheHit, { allowUnknown: true })
    : normalizeResultSource(result?.analysis_source, finalCacheHit, { allowUnknown: true });
  const firstSource = explicitFirstSource
    ? normalizeResultSource(explicitFirstSource, firstCacheHit, { allowUnknown: true })
    : finalSource;
  return {
    firstSource,
    finalSource,
    firstCacheHit,
    finalCacheHit,
    cacheKey: cacheInfo.cacheKey,
  };
};

export const getRunCacheStats = (run = {}) => {
  const runResults = Array.isArray(run?.results) ? run.results : [];
  const explicitCacheHitCount = toFiniteCount(run?.cache_hit_count);
  const computedCacheHitCount = runResults.reduce((sum, resultEntry) => {
    const cacheInfo = getResultCacheInfo(resultEntry);
    return cacheInfo.cacheHit ? sum + 1 : sum;
  }, 0);
  const count = explicitCacheHitCount !== null ? explicitCacheHitCount : computedCacheHitCount;
  const total = runResults.length;
  const explicitRate = toFiniteNumber(run?.cache_hit_rate);
  const rate =
    explicitRate !== null
      ? Math.max(0, Math.min(1, explicitRate))
      : total > 0
        ? count / total
        : 0;
  return { count, total, rate };
};

export const getRunTokenTotals = (run = {}) => {
  const runInput = toFiniteCount(run?.input_tokens_consumed);
  const runOutput = toFiniteCount(run?.output_tokens_consumed);
  const runTotal = toFiniteCount(run?.total_tokens_consumed);
  const hasRunTokenField = runInput !== null || runOutput !== null || runTotal !== null;
  const hasRunTokenValue = (runInput ?? 0) > 0 || (runOutput ?? 0) > 0 || (runTotal ?? 0) > 0;
  if (hasRunTokenField && hasRunTokenValue) {
    const total =
      runTotal !== null ? runTotal : runInput !== null && runOutput !== null ? runInput + runOutput : null;
    return { input: runInput, output: runOutput, total };
  }

  const runResults = Array.isArray(run?.results) ? run.results : [];
  let hasInput = false;
  let hasOutput = false;
  let hasTotal = false;
  let input = 0;
  let output = 0;
  let total = 0;
  runResults.forEach((result) => {
    const counts = getResultTokenCounts(result);
    if (counts.input !== null) {
      hasInput = true;
      input += counts.input;
    }
    if (counts.output !== null) {
      hasOutput = true;
      output += counts.output;
    }
    if (counts.total !== null) {
      hasTotal = true;
      total += counts.total;
    }
  });
  if (!hasInput && !hasOutput && !hasTotal) {
    return { input: null, output: null, total: null };
  }
  if (input <= 0 && output <= 0 && total <= 0) {
    return { input: null, output: null, total: null };
  }
  if (!hasTotal && hasInput && hasOutput) {
    total = input + output;
    hasTotal = true;
  }
  return {
    input: hasInput ? input : null,
    output: hasOutput ? output : null,
    total: hasTotal ? total : null,
  };
};

export const getRunIdentificationStats = (run = {}) => {
  // Scored at read time by the projection layer, so runs recorded before the
  // scorer existed carry it too — but a run fetched summary-only, or one whose
  // dataset has no manifest lines, carries nothing. All-null, like token totals.
  const stats = run && typeof run.identification === 'object' ? run.identification : null;
  const scored = toFiniteCount(stats?.identification_scored);
  if (!stats || scored === null || scored <= 0) {
    return { brandAccuracy: null, nameMatchRate: null };
  }
  // brand_accuracy is null when no lot in the run named a brand to be right
  // about. That is not 0% — it stays null so the pill renders muted.
  return {
    brandAccuracy: toFiniteNumber(stats?.brand_accuracy),
    nameMatchRate: toFiniteNumber(stats?.name_match_rate),
  };
};

const UNAVAILABLE_IDENTIFICATION = {
  available: false,
  scored: false,
  brandCorrect: null,
  nameF1: null,
  modelBrand: '',
  expectedBrand: '',
  expectedName: '',
};

export const getItemIdentification = (item = {}, result = {}, run = undefined) => {
  // The run pill says 89%; this says WHICH answers earned it. Both come from
  // the same read-time projection, so a lot the manifest cannot arbitrate
  // (a pallet, a blank line) carries no verdict at all — `scored: false`,
  // rendered muted, never as a miss.
  //
  // Three states, not two. A run whose dataset ships no manifest lines is
  // never scored at all: the projection attaches no `identification` summary,
  // so every item would otherwise read as "not scored" and blame a manifest
  // line nothing ever looked at. That run is `available: false` and shows no
  // badge. Pass `run` to get that distinction; without it every item the
  // projection skipped reads as "not scored".
  const row = item && typeof item === 'object' ? item : {};
  const lot = result && typeof result === 'object' ? result : {};
  if (run !== undefined && !(run && typeof run.identification === 'object' && run.identification)) {
    return UNAVAILABLE_IDENTIFICATION;
  }
  const hasVerdict =
    Object.prototype.hasOwnProperty.call(row, 'brand_correct') ||
    Object.prototype.hasOwnProperty.call(row, 'name_f1');
  if (!hasVerdict) {
    return {
      available: true,
      scored: false,
      brandCorrect: null,
      nameF1: null,
      modelBrand: '',
      expectedBrand: '',
      expectedName: '',
    };
  }
  // brand_correct is null when the manifest line names no brand to be right
  // about — distinct from false, and it must not read as a miss.
  const brandCorrect = typeof row.brand_correct === 'boolean' ? row.brand_correct : null;
  return {
    available: true,
    scored: true,
    brandCorrect,
    nameF1: toFiniteNumber(row.name_f1),
    modelBrand: typeof row.brand === 'string' ? row.brand.trim() : '',
    expectedBrand: typeof lot.expected_brand === 'string' ? lot.expected_brand : '',
    expectedName: typeof lot.expected_name === 'string' ? lot.expected_name : '',
  };
};

export const formatIdentificationTooltip = (identification = {}) => {
  // Shown on hover over the badge: the miss is only actionable next to the
  // claim it was graded against.
  if (identification && identification.available === false) {
    return 'Not scored: this run\'s dataset ships no manifest lines to grade answers against.';
  }
  if (!identification?.scored) {
    return 'Not scored: the manifest line for this lot names more than one product, or names nothing.';
  }
  const lines = [];
  if (identification.expectedName) lines.push(`Manifest: ${identification.expectedName}`);
  if (identification.expectedBrand) lines.push(`Expected brand: ${identification.expectedBrand}`);
  if (identification.modelBrand) lines.push(`Model brand: ${identification.modelBrand}`);
  if (identification.brandCorrect === null) lines.push('Manifest names no brand, so brand is not graded.');
  if (identification.nameF1 !== null) lines.push(`Name F1: ${identification.nameF1.toFixed(2)}`);
  return lines.join('\n');
};

export const getRunProgressMetrics = (run = {}) => {
  const rawTotal = toFiniteCount(run?.total_data_points ?? run?.data_points);
  const total = rawTotal !== null && rawTotal > 0 ? rawTotal : null;
  let successful = toFiniteCount(run?.successful_data_points);
  let skipped = toFiniteCount(run?.skipped_data_points);
  let attempted = toFiniteCount(run?.attempted_data_points);
  if (successful === null) successful = 0;
  if (skipped === null) skipped = 0;
  if (attempted === null) attempted = successful + skipped;
  if (total !== null) attempted = Math.min(total, attempted);

  const explicitPercent = toFiniteNumber(run?.progress_percent);
  const progressPercent =
    explicitPercent !== null
      ? Math.max(0, Math.min(100, explicitPercent))
      : total && total > 0
        ? Math.max(0, Math.min(100, (attempted / total) * 100))
        : 0;
  const avgDurationSeconds = toFiniteNumber(run?.avg_duration_seconds);
  const etaSeconds = toFiniteNumber(run?.eta_seconds);
  return {
    total,
    attempted,
    successful,
    skipped,
    progressPercent,
    avgDurationSeconds,
    etaSeconds,
    currentLot: run?.current_lot,
    currentIndex: toFiniteCount(run?.current_index),
    currentAttempt: toFiniteCount(run?.current_attempt),
    phase: run?.phase || 'running',
    lastError: run?.last_error || '',
    updatedAt: run?.updated_at || null,
    datasetTruncated: toBool(run?.dataset_truncated, false),
  };
};

export const getRunPointStats = (run = {}) => {
  const attempted = toFiniteCount(
    run?.attempted_data_points ?? run?.data_points ?? run?.iterations ?? run?.num_iterations,
  );
  const successful = toFiniteCount(
    run?.successful_data_points ?? (Array.isArray(run?.results) ? run.results.length : null),
  );
  const explicitSkipped = toFiniteCount(run?.skipped_data_points);
  const inferredSkipped =
    attempted !== null && successful !== null ? Math.max(0, attempted - successful) : null;
  const skipped = explicitSkipped !== null ? explicitSkipped : inferredSkipped !== null ? inferredSkipped : 0;
  return { attempted, successful, skipped };
};

export const formatRunDataPoints = (run = {}) => {
  const { attempted, successful, skipped } = getRunPointStats(run);
  const truncSuffix = run?.dataset_truncated ? ' (truncated)' : '';
  if (attempted !== null && successful !== null) {
    return skipped > 0 ? `${successful}/${attempted}${truncSuffix}` : `${attempted}${truncSuffix}`;
  }
  if (attempted !== null) return `${attempted}${truncSuffix}`;
  if (successful !== null) return `${successful}${truncSuffix}`;
  return '-';
};

export const getRunDataPointsHint = (run = {}) => {
  const { attempted, successful, skipped } = getRunPointStats(run);
  if (attempted !== null && successful !== null) {
    return skipped > 0
      ? `Successful ${successful} of ${attempted} (${skipped} skipped)`
      : `Successful ${successful} of ${attempted}`;
  }
  if (attempted !== null) return `Data points: ${attempted}`;
  if (successful !== null) return `Data points: ${successful}`;
  return '';
};
